import { useEffect, useReducer, useRef, useState, type CSSProperties } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { api, ApiError } from '../api';
import { aparecer, CURVA, fila, pop, resorte, suave } from '../components/movimiento';
import type { Vista } from '../components/Layout';
import Escaner from '../components/Escaner';
import { Encabezado, Icono, Numero, useConfirmar } from '../components/ui';
import { mensajeDeError } from '../lib/useApi';
import { useSesion } from '../lib/sesion';
import { botonPrimario, botonSecundario, c, mono, tarjeta as estiloTarjeta, tono, HUSO } from '../theme';
import { leerRutaOperar } from './operar/desdeEscaneo';
import {
  accion,
  aLeerDeCola,
  avance,
  esperaLectura,
  hora,
  inicial,
  lecturaSinCampo,
  NOMBRES,
  opciones,
  pedido,
  porOrden,
  previa,
  reducir,
  resumen,
  TEXTO_CONFIRMAR,
  textoLugar,
  tituloRechazo,
  type Estado,
  type Operacion,
  type Tarjeta,
} from './operar/pasos';

/**
 * Operar: cada operación física del depósito como un recorrido guiado por el
 * lector. Se escanea en orden, la pantalla valida cada lectura y lo único que
 * escribe es el botón de confirmar (o, al armar, cada etiqueta, que no mueve
 * stock). La lógica de pasos vive en `operar/pasos.ts`; acá sólo hay red y
 * dibujo. Ver docs/flujo-operativo-codigos-de-barras.md.
 */

const OPERACIONES: { op: Operacion; icono: string; texto: string }[] = [
  { op: 'recibir', icono: 'move_to_inbox', texto: 'Leer los pallets de un contenedor y confirmar la recepción' },
  { op: 'ubicar', icono: 'shelves', texto: 'Darle el primer lugar en el rack a un pallet recibido' },
  { op: 'mover', icono: 'swap_horiz', texto: 'Sacar un pallet de un nivel y llevarlo a otro' },
  { op: 'fraccionar', icono: 'call_split', texto: 'Llevar un pallet del rack a la estación' },
  { op: 'armar', icono: 'inventory', texto: 'Escanear las etiquetas de los capachos en la estación' },
  { op: 'egresar', icono: 'local_shipping', texto: 'Despachar un capacho o un pallet completo' },
];

const ahora = () => new Date().toLocaleTimeString('es-AR', { timeZone: HUSO, hour: '2-digit', minute: '2-digit' });

// Botones de 48 px como mínimo: se tocan parado, con guantes.
const grande: CSSProperties = { minHeight: 48, justifyContent: 'center', fontSize: 15 };

/**
 * El paso nuevo entra por la derecha y el viejo se va por la izquierda, como
 * pasar de hoja; al volver, al revés. `dir` es 1 para adelante y -1 para atrás.
 */
const pasar = {
  entra: (dir: number) => ({ opacity: 0, x: 48 * dir }),
  quieto: { opacity: 1, x: 0, transition: suave },
  sale: (dir: number) => ({ opacity: 0, x: -48 * dir, transition: { duration: 0.22, ease: CURVA } }),
};

export default function Operar({
  onIr,
  inicio,
  onInicioLeido,
}: {
  onIr: (v: Vista, id?: string) => void;
  /** mover~PLT-…: la operación y lo ya escaneado en la barra de arriba. */
  inicio?: string | null;
  onInicioLeido?: () => void;
}) {
  const [estado, despachar] = useReducer(reducir, undefined, inicial);
  const perfil = useSesion();
  const [texto, setTexto] = useState('');
  const [pendiente, setPendiente] = useState(false);
  const [camara, setCamara] = useState(false);
  // De dónde se abrió la cámara: el escáner crece desde ese botón.
  const [origenCamara, setOrigenCamara] = useState<{ x: number; y: number }>();
  const [confirmar, dialogo] = useConfirmar();
  const campo = useRef<HTMLInputElement | null>(null);
  const tecleado = useRef('');
  // El oyente de teclado vive entre renders: llama siempre al `leer` del estado actual.
  const leerActual = useRef(leer);
  leerActual.current = leer;
  const lee = esperaLectura(estado);
  const deCola = aLeerDeCola(estado);

  // Llegó desde la barra de arriba: arranca la operación con lo leído en cola.
  useEffect(() => {
    if (!inicio) return;
    const r = leerRutaOperar(inicio);
    if (r) despachar({ tipo: 'iniciar', op: r.op, codigos: r.codigos });
    onInicioLeido?.();
  }, [inicio, onInicioLeido]);

  // Lo de la cola entra solo, por la lectura de siempre, cuando llega el paso que lo usa.
  useEffect(() => {
    if (!deCola || pendiente) return;
    despachar({ tipo: 'desencolar' });
    void leerActual.current(deCola);
  }, [deCola, pendiente, estado]);

  // El campo del lector siempre tiene el foco: la pistola tipea donde esté.
  useEffect(() => {
    if (lee && !camara && !dialogo) campo.current?.focus();
  }, [estado, lee, camara, dialogo, pendiente]);

  // Si se tocó un botón y después dispara la pistola, la tecla vuelve al campo.
  // En las pantallas sin campo (elección, confirmar, comprobante) la lectura se
  // junta acá hasta el Enter: la pistola nunca dispara en el vacío.
  useEffect(() => {
    if (!estado.op || camara || dialogo) return;
    const alTeclear = (e: KeyboardEvent) => {
      const activo = document.activeElement;
      if (activo instanceof HTMLInputElement || activo instanceof HTMLTextAreaElement || activo instanceof HTMLSelectElement) return;
      const tecla = e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
      if (lee) {
        if (tecla) campo.current?.focus();
        return;
      }
      if (e.key === 'Enter' && tecleado.current) {
        // Que el Enter de la pistola no toque el botón que quedó con el foco.
        e.preventDefault();
        const codigo = tecleado.current;
        tecleado.current = '';
        void leerActual.current(codigo);
      } else if (tecla) tecleado.current += e.key;
    };
    document.addEventListener('keydown', alTeclear);
    return () => {
      document.removeEventListener('keydown', alTeclear);
      tecleado.current = '';
    };
  }, [lee, camara, dialogo, estado.op]);

  // Con guantes no se mira la pantalla: el celular vibra distinto si rechazó.
  useEffect(() => {
    const t = estado.tarjeta?.tono;
    if (estado.paso === 'hecho' && !estado.tarjeta) navigator.vibrate?.(60);
    else if (t) navigator.vibrate?.(t === 'err' ? [90, 60, 90] : 50);
  }, [estado.tarjeta, estado.paso]);

  async function leer(bruto: string) {
    const codigo = bruto.trim().toUpperCase();
    if (!codigo) return;
    if (pendiente) {
      despachar({ tipo: 'tarjeta', tarjeta: { tono: 'warn', titulo: 'Esperá la lectura anterior', codigo, lineas: ['Volvé a escanear en un segundo.'] } });
      return;
    }
    // Sin campo: en el comprobante arranca otra vuelta y esta es su primera lectura.
    let e = estado;
    if (!esperaLectura(e)) {
      const ev = lecturaSinCampo(e, codigo);
      if (!ev) return;
      despachar(ev);
      if (ev.tipo !== 'iniciar') return;
      e = reducir(e, ev);
    }
    const local = previa(e, codigo);
    if (local) return despachar({ tipo: 'tarjeta', tarjeta: local });

    setPendiente(true);
    try {
      if (e.paso === 'capachos' && e.pallet) {
        despachar({ tipo: 'armado', codigo, r: await api.armarCapacho(codigo, e.pallet.id) });
        return;
      }
      const h = await api.buscar(codigo);
      if (e.op === 'recibir' && h.tipo === 'recepcion' && h.contenedor && (!e.contenedor || e.contenedor === h.contenedor)) {
        despachar({ tipo: 'recepcion', codigo, contenedor: h.contenedor, lectura: await api.escanear(h.contenedor, codigo) });
        return;
      }
      // Para sugerir la OT (Mover) y listar lo que hay que armar (Armar).
      const conCapachos =
        e.paso === 'pallet' &&
        h.tipo === 'pallet' &&
        h.tipoPallet !== 'completo' &&
        (e.op === 'mover' || e.op === 'fraccionar' || e.op === 'armar');
      const frac = conCapachos ? await api.fraccionamiento(h.codigo) : null;
      // La salida DSP- de un completo: su pallet dice qué ubicación libera.
      const origen =
        e.paso === 'item' && h.tipo === 'capacho' && h.unidad === 'pallet' && h.palletOrigen
          ? await api.buscar(h.palletOrigen).catch(() => undefined)
          : undefined;
      despachar({ tipo: 'hallazgo', codigo, h, capachos: frac?.capachos, saldo: frac?.saldo, origen });
    } catch (error) {
      // Un 4xx es la respuesta a esta etiqueta; un corte de red se puede reintentar.
      const respuesta = error instanceof ApiError && error.status >= 400 && error.status < 500;
      despachar({
        tipo: 'tarjeta',
        codigo: respuesta ? codigo : undefined,
        tarjeta: { tono: 'err', titulo: respuesta ? tituloRechazo(e) : 'No se pudo consultar', codigo, lineas: [mensajeDeError(error)] },
      });
    } finally {
      setPendiente(false);
    }
  }

  async function confirmarPaso() {
    const a = accion(estado);
    if (!a || pendiente) return;
    setPendiente(true);
    const quien = { operador: perfil.nombre, hora: ahora() };
    try {
      // En los tres casos, si la respuesta se perdió pero el servidor ya lo
      // hizo (un reintento tras un corte), se re-consulta y se muestra hecho.
      if (a.tipo === 'recibir') {
        try {
          const r = await api.confirmarIngreso(a.contenedor);
          despachar({ tipo: 'confirmado', ...quien, ingresados: r.ingresados, nuevoEstado: r.nuevoEstado });
        } catch (e) {
          const r = await api.recepcion(a.contenedor).catch(() => null);
          if (!r?.confirmado) throw e;
          despachar({ tipo: 'confirmado', ...quien, ingresados: r.leidos, nuevoEstado: r.estado });
        }
      } else if (a.tipo === 'asignar') {
        try {
          await api.asignarPosicion(a.codigo, a.pallet, a.nivel, a.origen, a.motivo);
        } catch (e) {
          const h = await api.buscar(a.pallet).catch(() => null);
          if (h?.posicion !== a.codigo || h.nivel !== a.nivel) throw e;
        }
        despachar({ tipo: 'confirmado', ...quien });
      } else {
        try {
          await api.moverCapacho(a.id, 'Entregado');
          despachar({ tipo: 'confirmado', ...quien });
        } catch (e) {
          // Otro operario, o un reintento tras un corte: si ya salió, está hecho.
          if (!(e instanceof ApiError && e.status === 409)) throw e;
          const h = await api.buscar(a.id);
          if (h.estado !== 'Entregado') throw e;
          despachar({ tipo: 'confirmado', ...quien, yaHabia: true });
        }
      }
    } catch (e) {
      // Un 4xx es un no del servidor; sin respuesta no se sabe si llegó a guardar.
      const respuesta = e instanceof ApiError && e.status >= 400 && e.status < 500;
      despachar({ tipo: 'falloConfirmar', mensaje: mensajeDeError(e), incierto: !respuesta });
    } finally {
      setPendiente(false);
    }
  }

  async function deshacer(id: string) {
    const ok = await confirmar({
      titulo: `¿Deshacer el egreso de ${id}?`,
      detalle: 'Vuelve a Aceptado y las piezas vuelven al stock de RDM. Queda registrada la corrección con tu nombre.',
      boton: 'Deshacer egreso',
    });
    if (!ok) return;
    setPendiente(true);
    try {
      await api.moverCapacho(id, 'Aceptado');
      despachar({ tipo: 'deshecho', operador: perfil.nombre, hora: ahora() });
    } catch (e) {
      despachar({ tipo: 'tarjeta', tarjeta: { tono: 'err', titulo: 'No se pudo deshacer', codigo: id, lineas: [mensajeDeError(e)] } });
    } finally {
      setPendiente(false);
    }
  }

  const iniciar = (op: Operacion) => despachar({ tipo: 'iniciar', op });

  // Hacia dónde se movió el recorrido, para que el paso entre del lado que corresponde.
  const pasoN = estado.op ? avance(estado).n : 0;
  const pasoAnterior = useRef(pasoN);
  const dir = pasoN < pasoAnterior.current ? -1 : 1;
  useEffect(() => {
    pasoAnterior.current = pasoN;
  }, [pasoN]);

  // ------------------------------------------------------------ elegir operación
  if (!estado.op) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 900 }}>
        <Encabezado bajada="Elegí qué vas a hacer y escaneá en orden. Nada se guarda hasta tocar el botón verde de confirmar." />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 240px), 1fr))', gap: 12 }}>
          {OPERACIONES.map((o, i) => (
            <motion.button
              key={o.op}
              {...fila(i)}
              // La tarjeta tocada se estira hasta ser la cabecera verde del
              // recorrido (y vuelve a su lugar al salir): misma `layoutId`.
              layoutId={`operar-${o.op}`}
              transition={{ ...fila(i).transition, layout: resorte }}
              type="button"
              onClick={() => iniciar(o.op)}
              style={{
                ...estiloTarjeta,
                padding: '18px 18px',
                minHeight: 104,
                display: 'flex',
                gap: 14,
                alignItems: 'center',
                textAlign: 'left',
                cursor: 'pointer',
                color: c.texto,
              }}
            >
              <span style={{ width: 52, height: 52, borderRadius: 14, background: c.verdeSuave, display: 'grid', placeItems: 'center', flex: '0 0 52px' }}>
                <Icono nombre={o.icono} size={30} color={c.verde} />
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <strong style={{ fontSize: 18 }}>{NOMBRES[o.op]}</strong>
                <span style={{ fontSize: 13.5, color: c.texto2 }}>{o.texto}</span>
              </span>
            </motion.button>
          ))}
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------ recorrido
  const { n, m } = avance(estado);
  const { pide, esperada, formato } = pedido(estado);
  const hecho = estado.paso === 'hecho';
  const desdeEstacion = Boolean(estado.origen?.esEstacion);
  const faltanLeer = estado.recepcion ? estado.recepcion.pallets - estado.recepcion.leidos : 0;
  // Mientras se guarda o se consulta, salir dejaría la respuesta cayendo en otro paso.
  const quieto = { disabled: pendiente, opacity: pendiente ? 0.5 : 1 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 600, margin: '0 auto', width: '100%' }}>
      <motion.div
        layoutId={`operar-${estado.op}`}
        transition={resorte}
        style={{ background: c.verde, color: '#fff', borderRadius: 16, padding: '14px 16px 12px' }}
      >
        {/* El texto aparece cuando la caja ya casi llegó: estirado se lee deformado. */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2, delay: 0.14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <strong style={{ fontSize: 18, lineHeight: 1.2, flex: 1, minWidth: 0, whiteSpace: 'normal' }}>{NOMBRES[estado.op]}</strong>
          <AnimatePresence initial={false}>
          {!hecho && (
            <motion.button
              key="cancelar"
              {...pop}
              type="button"
              disabled={quieto.disabled}
              onClick={() => despachar({ tipo: 'cancelar' })}
              style={{ ...botonSecundario, minHeight: 44, flexShrink: 0, background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,.6)', opacity: quieto.opacity }}
            >
              Cancelar
            </motion.button>
          )}
          </AnimatePresence>
        </div>
        <div style={{ fontSize: 13.5, opacity: 0.9, marginTop: 4 }}>
          {hecho ? 'Listo' : <>Paso <Numero valor={n} /> de <Numero valor={m} /></>}
        </div>
        {/* La barra crece desde cero al entrar y recorre lo que avanzó cada paso. */}
        <div style={{ height: 5, background: 'rgba(255,255,255,.25)', borderRadius: 3, marginTop: 8, overflow: 'hidden' }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${m ? (n / m) * 100 : 0}%` }}
            transition={suave}
            style={{ height: '100%', background: '#fff' }}
          />
        </div>
        </motion.div>
      </motion.div>

      {/* Cada paso entra de nuevo: con la vista en la etiqueta y no en la
          pantalla, el movimiento es lo que avisa de reojo que la lectura
          pasó. `popLayout` y no `wait`: el paso nuevo se monta en el acto
          (esperar a que salga el viejo dejaría el campo sin montar y la
          pistola tipeando al vacío) y el viejo se va por encima, despegado. */}
      <div style={{ position: 'relative' }}>
      <AnimatePresence mode="popLayout" initial={false} custom={dir}>
      <motion.div
        key={`${estado.op}-${estado.paso}`}
        custom={dir}
        variants={pasar}
        initial="entra"
        animate="quieto"
        exit="sale"
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
      <Contexto estado={estado} />

      {!hecho && (
        <div>
          <div style={{ fontSize: 21, fontWeight: 600, lineHeight: 1.25 }}>{pide}</div>
          {(esperada || formato) && (
            <div style={{ fontSize: 13.5, color: c.texto3, marginTop: 4 }}>
              {esperada ? 'Tiene que ser ' : 'Formato: '}
              <span style={{ fontFamily: mono, color: c.texto, fontWeight: esperada ? 600 : 400 }}>{esperada ?? formato}</span>
            </div>
          )}
        </div>
      )}

      {!hecho && estado.cola?.[0] && !deCola && (
        <div style={{ fontSize: 13.5, color: c.texto2 }}>
          Ya escaneado: <span style={{ fontFamily: mono, fontWeight: 600, color: c.texto }}>{estado.cola[0]}</span> — se usa en el paso siguiente
        </div>
      )}

      {lee && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void leer(texto);
            setTexto('');
          }}
          style={{ display: 'flex', gap: 8 }}
        >
          <input
            // El paso que se va todavía tiene su campo montado un instante: al
            // desmontarse no puede dejar `campo` en null con el nuevo ya puesto.
            ref={(el) => {
              if (el) campo.current = el;
            }}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Escaneá o tipeá"
            aria-label="Código leído"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            enterKeyHint="go"
            style={{
              flex: 1,
              minWidth: 0,
              height: 56,
              padding: '0 16px',
              borderRadius: 14,
              border: `2px solid ${c.verde}`,
              fontFamily: mono,
              fontSize: 19,
              background: c.panel,
              color: c.texto,
            }}
          />
          <button
            type="button"
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              setOrigenCamara({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
              setCamara(true);
            }}
            aria-label="Escanear con la cámara" style={{ ...botonSecundario, minHeight: 56, minWidth: 56, justifyContent: 'center', padding: 0, borderRadius: 14 }}>
            <Icono nombre="photo_camera" size={26} />
          </button>
        </form>
      )}
      <AnimatePresence>
        {pendiente && lee && (
          <motion.div key="consultando" {...aparecer} style={{ fontSize: 13.5, color: c.texto2 }}>
            Consultando…
          </motion.div>
        )}
      </AnimatePresence>

      {/* La tarjeta vieja sale antes de que entre la nueva: dos a la vez se apilan y la pantalla salta. */}
      <AnimatePresence mode="wait" initial={false}>
        {estado.tarjeta && (
          <TarjetaLectura
            key={[estado.tarjeta.titulo, estado.tarjeta.codigo, ...estado.tarjeta.lineas].join('|')}
            t={estado.tarjeta}
            onOfrecer={iniciar}
            onEgresarIgual={() => despachar({ tipo: 'continuar' })}
          />
        )}
      </AnimatePresence>

      {/* Recibir: el conteo y el paso a confirmar. */}
      {estado.op === 'recibir' && estado.paso === 'pallet' && (
        <>
          {estado.recepcion && (
            <div style={{ ...estiloTarjeta, padding: 16, display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 10px' }}>
              {/* El número cambia con cada pallet leído: se lo ve subir. */}
              <span style={{ fontSize: 34, fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', display: 'inline-block' }}>
                <Numero valor={estado.recepcion.leidos} /> de <Numero valor={estado.recepcion.pallets} />
              </span>
              <span style={{ color: c.texto2 }}>leídos de {estado.contenedor}</span>
            </div>
          )}
          <AnimatePresence initial={false}>
            {(estado.recepcion?.leidos ?? 0) > 0 && (
              <motion.button key="continuar" {...aparecer} type="button" onClick={() => despachar({ tipo: 'continuar' })} style={{ ...botonPrimario, ...grande }}>
                Continuar a confirmar
              </motion.button>
            )}
          </AnimatePresence>
          {estado.contenedor && (
            <button type="button" onClick={() => onIr('contenedores')} style={{ ...botonSecundario, ...grande }}>
              Ajustar piezas en Nuevo contenedor
            </button>
          )}
        </>
      )}

      {/* Mover: qué se hace con el pallet. La OT sugiere, no obliga. */}
      {estado.paso === 'eleccion' && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} role="radiogroup" aria-label="Qué hacés con este pallet">
            {opciones(estado).map((op) => {
              const sel = estado.modo === op;
              const s = estado.sugerida;
              return (
                <button
                  key={op}
                  type="button"
                  role="radio"
                  aria-checked={sel}
                  onClick={() => despachar({ tipo: 'elegir', modo: op })}
                  style={{
                    ...estiloTarjeta,
                    position: 'relative',
                    padding: '14px 16px',
                    minHeight: 64,
                    textAlign: 'left',
                    cursor: 'pointer',
                    color: c.texto,
                    border: `1px solid ${c.borde}`,
                    background: sel ? c.verdeTinte : c.panel,
                  }}
                >
                  {/* El marco verde de la elegida viaja de una opción a la otra. */}
                  {sel && (
                    <motion.span
                      layoutId="operar-eleccion"
                      transition={resorte}
                      aria-hidden="true"
                      style={{ position: 'absolute', inset: -1, borderRadius: 16, border: `2px solid ${c.verde}`, pointerEvents: 'none' }}
                    />
                  )}
                  <strong style={{ fontSize: 16 }}>
                    {op === 'fraccionar' ? 'Preparar fraccionamiento' : desdeEstacion ? 'Devolver a rack' : 'Reposicionar'}
                  </strong>
                  <div style={{ fontSize: 13.5, color: c.texto2, marginTop: 2 }}>
                    {op === 'fraccionar'
                      ? s
                        ? `${s.ot ?? 'Hay capachos'} ya lo eligió: ${s.capachos} capacho${s.capachos === 1 ? '' : 's'} aceptado${s.capachos === 1 ? '' : 's'}`
                        : 'Llevarlo a la estación'
                      : 'Llevarlo a otro nivel de rack o a una zona de piso'}
                  </div>
                </button>
              );
            })}
          </div>
          {estado.pallet?.tipo !== 'completo' && !desdeEstacion && (
            <div style={{ fontSize: 13, color: c.texto2 }}>Si elegís otra opción que la sugerida, queda registrado junto a la sugerencia.</div>
          )}
          <button type="button" onClick={() => despachar({ tipo: 'continuar' })} style={{ ...botonPrimario, ...grande }}>
            Continuar
          </button>
        </>
      )}

      {/* Armar: lo que hay que armar, agrupado por OT. */}
      {estado.paso === 'capachos' && (
        <>
          {porOrden(estado.capachos ?? []).map((g) => (
            <div key={g.orden ?? ''} style={{ ...estiloTarjeta, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <strong style={{ fontSize: 15 }}>
                {g.orden ?? 'Sin OT'} · <Numero valor={g.armados} /> de <Numero valor={g.total} /> armados
              </strong>
              {(estado.capachos ?? [])
                .filter((x) => (x.orden ?? '') === (g.orden ?? ''))
                .map((x) => (
                  <div key={x.id} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 32 }}>
                    {/* Al armarse, el círculo vacío se cambia por el tilde con un pulso. */}
                    <motion.span key={x.armadoEn ? 'si' : 'no'} {...pop} style={{ display: 'grid' }}>
                      <Icono nombre={x.armadoEn ? 'check_circle' : 'radio_button_unchecked'} size={22} color={x.armadoEn ? c.verde : c.texto4} />
                    </motion.span>
                    <span style={{ fontFamily: mono, fontWeight: 600 }}>{x.etiqueta ?? x.id}</span>
                    <span style={{ color: c.texto2, fontSize: 13.5 }}>
                      {x.piezas} pz{x.armadoEn ? ` · ${x.armadoPor ?? ''} ${hora(x.armadoEn)}` : ''}
                    </span>
                  </div>
                ))}
            </div>
          ))}
          <button
            type="button"
            onClick={() => despachar({ tipo: 'terminar', operador: perfil.nombre, hora: ahora() })}
            style={{ ...botonPrimario, ...grande }}
          >
            Terminar
          </button>
          {estado.pallet && (
            <button type="button" onClick={() => onIr('frac', estado.pallet!.id)} style={{ ...botonSecundario, ...grande }}>
              Armado manual en la ficha del pallet
            </button>
          )}
        </>
      )}

      {/* Confirmar: el único botón que escribe. */}
      {estado.paso === 'confirmar' && (
        <>
          <Filas filas={resumen(estado)} />
          {estado.op === 'recibir' && faltanLeer > 0 && (
            <div style={{ borderRadius: 12, border: `1px solid ${tono('warn').bd}`, background: tono('warn').bg, color: tono('warn').fg, padding: '10px 14px', fontSize: 14.5, fontWeight: 600 }}>
              Faltan leer {faltanLeer} de {estado.recepcion!.pallets} pallets: se confirman sólo los leídos.
            </div>
          )}
          <div style={{ fontSize: 13, color: c.texto2 }}>Lo único que guarda es este botón. El servidor vuelve a validar todo.</div>
          <button
            type="button"
            disabled={pendiente}
            onClick={() => void confirmarPaso()}
            style={{ ...botonPrimario, minHeight: 60, justifyContent: 'center', fontSize: 17, opacity: pendiente ? 0.6 : 1, cursor: pendiente ? 'wait' : 'pointer' }}
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span key={pendiente ? 'confirmando' : 'confirmar'} {...aparecer}>
                {pendiente ? 'Confirmando…' : TEXTO_CONFIRMAR[estado.op]}
              </motion.span>
            </AnimatePresence>
          </button>
        </>
      )}

      {/* Comprobante. */}
      {hecho && estado.comprobante && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: c.verdeOscuro, fontSize: 20, fontWeight: 700 }}>
            <motion.span {...pop} transition={{ ...pop.transition, delay: 0.12 }} style={{ display: 'grid' }}>
              <Icono nombre="check_circle" size={30} color={c.verde} />
            </motion.span>
            {estado.comprobante.titulo}
          </div>
          <Filas filas={estado.comprobante.filas} />
          {estado.comprobante.siguientes.map((s, i) => (
            <button key={s.op + s.texto} type="button" onClick={() => iniciar(s.op)} style={{ ...(i === 0 ? botonPrimario : botonSecundario), ...grande }}>
              {s.texto}
            </button>
          ))}
          {estado.comprobante.deshacer && (
            <button
              type="button"
              disabled={pendiente}
              onClick={() => void deshacer(estado.comprobante!.deshacer!)}
              style={{ ...botonSecundario, ...grande, color: c.rojo, borderColor: c.rojo }}
            >
              Deshacer egreso
            </button>
          )}
          <button type="button" onClick={() => despachar({ tipo: 'cancelar' })} style={{ ...botonSecundario, ...grande }}>
            Volver al inicio
          </button>
        </>
      )}

      {!hecho && (
        <button
          type="button"
          disabled={quieto.disabled}
          onClick={() => despachar({ tipo: 'volver' })}
          style={{ ...botonSecundario, ...grande, alignSelf: 'flex-start', opacity: quieto.opacity }}
        >
          <Icono nombre="arrow_back" size={20} />
          Volver
        </button>
      )}
      </motion.div>
      </AnimatePresence>
      </div>

      {/* Afuera del paso animado: el escáner es fijo y una transformación en
          un padre lo encerraría en la caja del paso. */}
      <AnimatePresence>
      {camara && (
        <Escaner
          key="camara"
          origen={origenCamara}
          onCerrar={() => setCamara(false)}
          onLeer={(codigo) => {
            setCamara(false);
            void leer(codigo);
          }}
        />
      )}
      </AnimatePresence>
      {dialogo}
    </div>
  );
}

/** Lo ya validado en pasos anteriores, para no perder de vista qué se mueve. */
function Contexto({ estado }: { estado: Estado }) {
  const filas: [string, string][] = [];
  if (estado.paso === 'hecho' || estado.paso === 'confirmar') return null;
  if (estado.origen) filas.push([estado.op === 'armar' ? 'Estación' : 'Origen', textoLugar(estado.origen)]);
  if (estado.pallet) filas.push(['Pallet', `${estado.pallet.id} · ${estado.pallet.producto}`]);
  if (!filas.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {filas.map(([k, v]) => (
        // Radio de pastilla en un renglón; si el producto es largo y parte en dos, queda una caja redondeada y no un óvalo.
        <span key={k} style={{ fontSize: 13, padding: '6px 12px', borderRadius: 17, background: c.verdeSuave, border: `1px solid ${c.verdeBorde}`, color: c.texto }}>
          <span style={{ color: c.texto2 }}>{k}: </span>
          <span style={{ fontFamily: mono, fontWeight: 600 }}>{v}</span>
        </span>
      ))}
    </div>
  );
}

function TarjetaLectura({ t, onOfrecer, onEgresarIgual }: { t: Tarjeta; onOfrecer: (op: Operacion) => void; onEgresarIgual: () => void }) {
  const colores = tono(t.tono);
  const icono = t.tono === 'ok' ? 'check_circle' : t.tono === 'err' ? 'cancel' : 'warning';
  return (
    // Entra con cada lectura nueva (la clave la pone quien la dibuja).
    <motion.div {...aparecer} role="status" aria-live="polite" style={{ borderRadius: 16, border: `2px solid ${colores.bd}`, background: colores.bg, padding: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icono nombre={icono} size={26} color={colores.fg} />
        <strong style={{ fontSize: 17, color: colores.fg }}>{t.titulo}</strong>
      </div>
      {t.codigo && <div style={{ fontFamily: mono, fontWeight: 600, fontSize: 15 }}>{t.codigo}</div>}
      {t.lineas.filter(Boolean).map((l, i) => (
        <div key={i} style={{ fontSize: 14.5, color: c.texto }}>
          {l}
        </div>
      ))}
      {t.tono === 'err' && <div style={{ fontSize: 13, color: c.texto2 }}>No se modificó nada. Seguís en el mismo paso.</div>}
      {t.ofrecer?.map((op) => (
        <button key={op} type="button" onClick={() => onOfrecer(op)} style={{ ...botonSecundario, ...grande, marginTop: 4 }}>
          Ir a {NOMBRES[op]}
        </button>
      ))}
      {t.egresarIgual && (
        <button type="button" onClick={onEgresarIgual} style={{ ...botonPrimario, ...grande, marginTop: 4 }}>
          Egresar igual
        </button>
      )}
    </motion.div>
  );
}

function Filas({ filas }: { filas: [string, string][] }) {
  return (
    <dl style={{ ...estiloTarjeta, margin: 0, padding: 16, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 14px', fontSize: 15 }}>
      {filas.map(([k, v]) => (
        <div key={k} style={{ display: 'contents' }}>
          <dt style={{ color: c.texto2 }}>{k}</dt>
          <dd style={{ margin: 0, fontWeight: 600, overflowWrap: 'anywhere' }}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}
